import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { branchAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ModernSelect, LoadingSpinner } from './SharedComponents';

const Container = styled.div`
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.light};
  font-size: 0.9rem;
`;

const EmptyContainer = styled.div`
  padding: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.light};
  font-size: 0.9rem;
  
  div {
    margin-top: ${props => props.theme.spacing.xs};
    font-size: 0.8rem;
    opacity: 0.8;
  }
`;

const BranchInfo = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.secondary}30;
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.text.secondary};
  border: 1px solid ${props => props.theme.colors.border.light};
  
  strong {
    color: ${props => props.theme.colors.primary};
  }
`;

const BranchSelector = ({ 
  selectedBranchId, 
  value, 
  onBranchChange, 
  onChange, 
  showAllOption = false,
  placeholder = "Select branch",
  multiple = false 
}) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const { canAccessBranch, accessibleBranches } = useAuth();

  // Handle both prop naming conventions
  const currentValue = value !== undefined ? value : selectedBranchId;
  const handleChange = onChange || onBranchChange;

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="16px" />
        Loading branches...
      </LoadingContainer>
    );
  }

  // Filter branches based on user permissions
  const availableBranches = accessibleBranches === 'ALL_BRANCHES' 
    ? branches // Show all branches for admins and managers
    : branches.filter(branch => 
        accessibleBranches.includes(branch.id)
      );

  if (availableBranches.length === 0) {
    return (
      <EmptyContainer>
        No accessible branches
        {branches.length > 0 && (
          <div>
            Found {branches.length} branches, but none accessible to your role
          </div>
        )}
      </EmptyContainer>
    );
  }

  const handleSelectChange = (e) => {
    if (multiple) {
      // Handle multiple selection (for future use)
      const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
      handleChange(selectedOptions);
    } else {
      // Handle single selection
      const selectedValue = e.target.value ? parseInt(e.target.value) : null;
      handleChange(selectedValue);
    }
  };

  const selectedBranch = currentValue && availableBranches.find(b => b.id === currentValue);

  return (
    <Container>
      <ModernSelect
        value={currentValue || ''}
        onChange={handleSelectChange}
        multiple={multiple}
      >
        {!multiple && (
          <option value="">{placeholder}</option>
        )}
        {showAllOption && !multiple && (
          <option value="">All Branches</option>
        )}
        {availableBranches.map((branch) => (
          <option key={branch.id} value={branch.id}>
            {branch.name} {branch.location && `(${branch.location})`}
          </option>
        ))}
      </ModernSelect>
      
      {/* Branch info display */}
      {selectedBranch && (
        <BranchInfo>
          <strong>Selected:</strong> {selectedBranch.name}
          {selectedBranch.location && ` - ${selectedBranch.location}`}
        </BranchInfo>
      )}
    </Container>
  );
};

export default BranchSelector;